import { formatCurrencyGbp, formatNumber } from "./calculations";

function KPI({ label, value, positive = true, subtext = "" }) {
  return (
    <div className="rounded-lg border border-border bg-paper/40 p-3.5 md:p-4">
      <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
      <div className={`mt-2 font-display text-xl sm:text-2xl font-extrabold ${positive ? "text-lime-700" : "text-red-700"}`}>
        {value}
      </div>
      {subtext ? <div className="mt-1 text-xs text-muted-foreground">{subtext}</div> : null}
    </div>
  );
}

export default function ResultsSummary({ calc }) {
  const s = calc.summary;
  const positive = s.perCampaignNetValue >= 0;
  const goodMultiple = s.valuePerPound >= 1;

  return (
    <section className="rounded-xl border border-border bg-card p-4 md:p-5">
      <h3 className="font-display font-bold text-xl text-ink">Live results</h3>
      <p className="mt-1 text-xs sm:text-sm text-muted-foreground">
        No subscription. No access fee. £350 per successful hire only.
      </p>

      {/* Hero value-for-money banner */}
      {s.talentPoolCostPerCampaign > 0 && (
        <div className={`mt-4 rounded-xl p-4 md:p-5 border-2 ${goodMultiple ? "border-lime-500 bg-lime-50 dark:bg-lime-950/30" : "border-amber-400 bg-amber-50 dark:bg-amber-950/30"}`}>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Value for money signal</div>
          <div className={`mt-1 font-display text-4xl sm:text-5xl font-extrabold ${goodMultiple ? "text-lime-700" : "text-amber-700"}`}>
            {s.valuePerPound > 0 ? `${formatNumber(s.valuePerPound, 1)}×` : "—"}
          </div>
          <div className="mt-1 text-sm font-medium text-ink">
            Indicative internal value returned per £1 of Talent Pool fee
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            {formatCurrencyGbp(s.internalCapacityValue)} indicative value of hours saved&nbsp;
            vs&nbsp;{formatCurrencyGbp(s.talentPoolCostPerCampaign)} Talent Pool cost per campaign
          </div>
        </div>
      )}

      {/* Primary KPIs: hours and value vs cost */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <KPI
          label="Staff-hours released per campaign"
          value={`${formatNumber(s.internalCapacityReleasedHours, 1)} hrs`}
          positive={s.internalCapacityReleasedHours >= 0}
          subtext={`${formatNumber(s.workingPersonDaysReleased, 1)} working days`}
        />
        <KPI
          label="Indicative value of hours saved"
          value={formatCurrencyGbp(s.internalCapacityValue)}
          positive={s.internalCapacityValue >= 0}
          subtext="Based on loaded hourly rate of staff involved"
        />
        <KPI
          label="Talent Pool cost per campaign"
          value={formatCurrencyGbp(s.talentPoolCostPerCampaign)}
          positive={s.talentPoolCostPerCampaign <= s.internalCapacityValue}
          subtext={`£${formatNumber(s.feePerSuccessfulHire, 0)} × ${formatNumber(s.successfulRailwayHires, 0)} expected hires`}
        />
        <KPI
          label="Net value per campaign"
          value={formatCurrencyGbp(s.perCampaignNetValue)}
          positive={positive}
          subtext={`${formatCurrencyGbp(s.annualisedValue)} annualised`}
        />
      </div>

      {/* Secondary detail */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KPI
          label="Interview panel-days avoided"
          value={formatNumber(s.interviewPanelDaysAvoided, 1)}
          positive={s.interviewPanelDaysAvoided >= 0}
          subtext="Panel-day equivalents"
        />
        <KPI
          label="Operational manager-days released"
          value={formatNumber(s.operationalManagerDaysReleased, 1)}
          positive={s.operationalManagerDaysReleased >= 0}
        />
        <KPI
          label="Annualised net value"
          value={formatCurrencyGbp(s.annualisedValue)}
          positive={s.annualisedValue >= 0}
        />
      </div>

      {!positive && (
        <div className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          Under current assumptions, the talent-pool route costs more than the conventional route. Review funnel volume, interview assumptions and route-specific costs before approval.
        </div>
      )}

      <p className="mt-4 text-[11px] text-muted-foreground">
        Indicative value represents internal capacity released based on loaded staff cost. Whether this translates to a cashable budget saving depends on whether equivalent spend is genuinely avoided. Railway Careers UK supports candidate identification and pre-screening only; employers retain all legal, regulatory and appointment decisions.
      </p>
    </section>
  );
}
